import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Code, Download, Book, Terminal, Zap, Shield, 
  Globe, FileText, ChevronRight, Copy, Check,
  ExternalLink, ArrowRight, Rocket, Clock, Users
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_factura-mexico-app/artifacts/u9456sea_Logo%20CIA.jpg";

const DeveloperPortal = () => {
  const [copiedCode, setCopiedCode] = useState(null);
  const [activeSDK, setActiveSDK] = useState('python');

  const copyCode = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Código copiado');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const downloadSDK = (language) => {
    window.open(`${API_URL}/api/sdk/${language}`, '_blank');
    toast.success(`Descargando SDK de ${language}`);
  };

  const sdkExamples = {
    python: {
      name: 'Python',
      icon: '🐍',
      install: 'pip install ciafactura',
      quickstart: `from ciafactura import CIAFactura

# Inicializar cliente
client = CIAFactura(
    api_key="cia_pk_tu_api_key",
    api_secret="cia_sk_tu_api_secret"
)

# Crear factura
invoice = client.invoices.create(
    receptor_rfc="XAXX010101000",
    receptor_name="Cliente SA de CV",
    receptor_email="cliente@email.com",
    items=[{
        "description": "Servicio de consultoría",
        "unit_price": 5000.00
    }]
)

print(f"Factura creada: {invoice.cfdi_uuid}")
print(f"Total: \${invoice.total:,.2f} MXN")`,
      download: 'python'
    },
    javascript: {
      name: 'JavaScript',
      icon: '🟨',
      install: 'npm install @ciafactura/sdk',
      quickstart: `import { CIAFactura } from '@ciafactura/sdk';

// Inicializar cliente
const client = new CIAFactura({
  apiKey: 'cia_pk_tu_api_key',
  apiSecret: 'cia_sk_tu_api_secret'
});

// Crear factura
const invoice = await client.invoices.create({
  receptorRfc: 'XAXX010101000',
  receptorName: 'Cliente SA de CV',
  receptorEmail: 'cliente@email.com',
  items: [{
    description: 'Servicio de consultoría',
    unitPrice: 5000.00
  }]
});

console.log(\`Factura creada: \${invoice.cfdiUuid}\`);
console.log(\`Total: $\${invoice.total}\`);`,
      download: 'javascript'
    },
    php: {
      name: 'PHP',
      icon: '🐘',
      install: 'composer require ciafactura/sdk',
      quickstart: `<?php
use CIAFactura\\Client;

// Inicializar cliente
$client = new Client(
    'cia_pk_tu_api_key',
    'cia_sk_tu_api_secret'
);

// Crear factura
$invoice = $client->invoices->create([
    'receptor_rfc' => 'XAXX010101000',
    'receptor_name' => 'Cliente SA de CV',
    'receptor_email' => 'cliente@email.com',
    'items' => [[
        'description' => 'Servicio de consultoría',
        'unit_price' => 5000.00
    ]]
]);

echo "Factura creada: " . $invoice->cfdi_uuid . "\\n";
echo "Total: " . $invoice->getFormattedTotal() . "\\n";`,
      download: 'php'
    },
    curl: {
      name: 'cURL',
      icon: '💻',
      install: null,
      quickstart: `curl -X POST "${API_URL}/api/v1/invoices" \\
  -H "X-API-Key: cia_pk_tu_api_key" \\
  -H "X-API-Secret: cia_sk_tu_api_secret" \\
  -H "Content-Type: application/json" \\
  -d '{
    "receptor_rfc": "XAXX010101000",
    "receptor_name": "Cliente SA de CV",
    "receptor_email": "cliente@email.com",
    "receptor_tax_regime": "601",
    "receptor_zip_code": "06600",
    "items": [{
      "description": "Servicio de consultoría",
      "unit_price": 5000.00
    }]
  }'`,
      download: null
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900">
      {/* Navigation */}
      <nav className="bg-slate-900/80 backdrop-blur-md border-b border-slate-700 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="CIA Factura" className="h-10 w-auto rounded-lg" />
              <div>
                <span className="font-bold text-xl text-white">CIA Factura</span>
                <span className="text-indigo-400 text-sm block">Developers</span>
              </div>
            </Link>
            <div className="hidden md:flex items-center gap-4">
              <a href="#sdk" className="text-slate-300 hover:text-white transition-colors">SDKs</a>
              <a href="#docs" className="text-slate-300 hover:text-white transition-colors">Documentación</a>
              <a href="#examples" className="text-slate-300 hover:text-white transition-colors">Ejemplos</a>
              <Link to="/register">
                <Button className="bg-indigo-500 hover:bg-indigo-600">
                  Obtener API Keys
                </Button>
              </Link>
            </div>
            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <Link to="/register">
                <Button size="sm" className="bg-indigo-500 hover:bg-indigo-600">
                  API Keys
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-indigo-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-indigo-500/20 text-indigo-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Code className="w-4 h-4" />
              API v1 Disponible
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Integra facturación electrónica en <span className="text-indigo-400">minutos</span>
            </h1>
            <p className="text-xl text-slate-300 mb-8">
              API REST simple y potente para emitir CFDIs. SDKs oficiales en Python, JavaScript y PHP. 
              Documentación completa y ejemplos listos para copiar.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#sdk">
                <Button size="lg" className="bg-indigo-500 hover:bg-indigo-600 text-white w-full sm:w-auto">
                  <Download className="w-5 h-5 mr-2" />
                  Descargar SDK
                </Button>
              </a>
              <a href="#docs">
                <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800 w-full sm:w-auto">
                  <Book className="w-5 h-5 mr-2" />
                  Ver Documentación
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-slate-800/50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Integración Rápida</h3>
              <p className="text-slate-400">
                Emite tu primera factura en menos de 5 minutos con nuestros SDKs
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">100% Válido SAT</h3>
              <p className="text-slate-400">
                CFDIs timbrados por PAC autorizado, cumpliendo todas las normas fiscales
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Webhooks</h3>
              <p className="text-slate-400">
                Recibe notificaciones en tiempo real cuando tus facturas sean procesadas
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SDKs Section */}
      <section className="py-20" id="sdk">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              SDKs Oficiales
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Librerías nativas para los lenguajes más populares. Tipado completo, manejo de errores y documentación incluida.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
            {['python', 'javascript', 'php'].map((lang) => (
              <Card 
                key={lang}
                className={`bg-slate-800 border-slate-700 cursor-pointer transition-all ${
                  activeSDK === lang ? 'ring-2 ring-indigo-500' : 'hover:border-slate-600'
                }`}
                onClick={() => setActiveSDK(lang)}
              >
                <CardContent className="pt-6 text-center">
                  <span className="text-4xl mb-3 block">{sdkExamples[lang].icon}</span>
                  <h3 className="text-xl font-semibold text-white mb-2">{sdkExamples[lang].name}</h3>
                  <code className="text-xs text-slate-400 bg-slate-900 px-2 py-1 rounded">
                    {sdkExamples[lang].install}
                  </code>
                  <Button 
                    className="w-full mt-4 bg-indigo-500 hover:bg-indigo-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadSDK(sdkExamples[lang].download);
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Descargar
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Quick Start Code */}
          <div className="max-w-4xl mx-auto" id="examples">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-white">
                  <span className="flex items-center gap-2">
                    <Terminal className="w-5 h-5" />
                    Quickstart - {sdkExamples[activeSDK].name}
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-slate-400 hover:text-white"
                    onClick={() => copyCode(sdkExamples[activeSDK].quickstart, 'quickstart')}
                  >
                    {copiedCode === 'quickstart' ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-sm text-slate-300 font-mono whitespace-pre-wrap">
                    {sdkExamples[activeSDK].quickstart}
                  </pre>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* API Documentation Preview */}
      <section className="py-20 bg-slate-800/50" id="docs">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Documentación de la API
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              API REST con endpoints claros y respuestas predecibles
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {/* Base URL */}
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400 text-sm">Base URL</span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-slate-400"
                    onClick={() => copyCode(`${API_URL}/api/v1`, 'baseurl')}
                  >
                    {copiedCode === 'baseurl' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <code className="text-lg text-indigo-400 font-mono">{API_URL}/api/v1</code>
              </CardContent>
            </Card>

            {/* Endpoints */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Endpoints</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Create Invoice */}
                <div className="border border-slate-700 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded">POST</span>
                    <code className="text-white font-mono">/invoices</code>
                  </div>
                  <p className="text-slate-400 text-sm">Crea una nueva factura CFDI 4.0</p>
                </div>

                {/* Get Invoice */}
                <div className="border border-slate-700 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold rounded">GET</span>
                    <code className="text-white font-mono">/invoices/{'{invoice_id}'}</code>
                  </div>
                  <p className="text-slate-400 text-sm">Obtiene los detalles de una factura</p>
                </div>

                {/* Download PDF */}
                <div className="border border-slate-700 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold rounded">GET</span>
                    <code className="text-white font-mono">/invoices/{'{invoice_id}'}/pdf</code>
                  </div>
                  <p className="text-slate-400 text-sm">Descarga el PDF de la factura</p>
                </div>

                {/* Download XML */}
                <div className="border border-slate-700 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold rounded">GET</span>
                    <code className="text-white font-mono">/invoices/{'{invoice_id}'}/xml</code>
                  </div>
                  <p className="text-slate-400 text-sm">Descarga el XML del CFDI</p>
                </div>
              </CardContent>
            </Card>

            {/* Authentication */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Shield className="w-5 h-5 text-indigo-400" />
                  Autenticación
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-400 mb-4">
                  Todas las solicitudes requieren estos headers:
                </p>
                <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm">
                  <div className="text-slate-300">
                    <span className="text-indigo-400">X-API-Key:</span> cia_pk_tu_api_key
                  </div>
                  <div className="text-slate-300">
                    <span className="text-indigo-400">X-API-Secret:</span> cia_sk_tu_api_secret
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <Card className="bg-gradient-to-r from-indigo-600 to-purple-600 border-0">
            <CardContent className="py-12 text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                ¿Listo para integrar?
              </h2>
              <p className="text-indigo-100 text-lg mb-8 max-w-xl mx-auto">
                Crea tu cuenta gratuita, obtén tus API Keys y empieza a facturar en minutos.
                Plan Empresarial incluye acceso completo a la API.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button size="lg" className="bg-white text-indigo-600 hover:bg-indigo-50 w-full sm:w-auto">
                    <Rocket className="w-5 h-5 mr-2" />
                    Crear Cuenta Gratis
                  </Button>
                </Link>
                <Link to="/">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 w-full sm:w-auto">
                    Ver Planes
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="CIA Factura" className="h-8 w-auto rounded" />
              <span className="text-white font-semibold">CIA Factura℗ Developers</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-slate-400 hover:text-white text-sm">Inicio</Link>
              <Link to="/dashboard/api-dashboard" className="text-slate-400 hover:text-white text-sm">Dashboard API</Link>
              <a href="mailto:developers@ciafactura.com" className="text-slate-400 hover:text-white text-sm">Soporte</a>
            </div>
            <p className="text-slate-500 text-sm">
              © 2026 CIA Labs ℗. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DeveloperPortal;
